import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { ordersApi } from '../services/api';
import { fmtDate, fmtCurrency, statusClass } from '../utils/helpers';
import TopNav from '../components/layout/TopNav';

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query) return;
    setLoading(true);
    // Fetch all orders and filter client-side, matching the same search fields
    // as the prototype. In a larger system, add a search endpoint to the API.
    ordersApi.getAll({}).then(all => {
      const q = query.toLowerCase();
      const filtered = all.filter(o =>
        String(o.fileNumber).includes(q) ||
        (o.loanNumber || '').toLowerCase().includes(q) ||
        (o.borrowerLastName || '').toLowerCase().includes(q) ||
        (o.lender || '').toLowerCase().includes(q) ||
        (o.street || '').toLowerCase().includes(q) ||
        (o.city || '').toLowerCase().includes(q) ||
        (o.state || '').toLowerCase().includes(q)
      );
      setResults(filtered);
    }).catch(console.error).finally(() => setLoading(false));
  }, [query]);

  function highlight(text) {
    if (!text || !query) return text;
    const re = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = String(text).split(re);
    return parts.map((part, i) =>
      re.test(part) ? <mark key={i} className="highlight">{part}</mark> : part
    );
  }

  return (
    <div>
      <TopNav />
      <div className="page-content">
        <div style={{ fontSize: 14, color: '#666', marginBottom: '1rem' }}>
          <strong>{results.length}</strong> result{results.length !== 1 ? 's' : ''} for{' '}
          <strong>"{query}"</strong> — searched file number, loan number, borrower last name, lender, address
        </div>

        <div className="card">
          <div className="queue-wrap">
            <table className="queue-table">
              <thead>
                <tr>
                  <th>File #</th><th>File type</th><th>Date ordered</th><th>Borrower last name</th>
                  <th>Loan number</th><th>State</th><th>Closing date</th><th>Status</th>
                  <th>Assigned</th><th>Loan amount</th><th>Lender</th><th>Broker</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={12} style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>Searching...</td></tr>
                ) : results.length === 0 ? (
                  <tr><td colSpan={12} style={{ padding: '2rem', textAlign: 'center', color: '#aaa' }}>No files found matching "{query}"</td></tr>
                ) : results.map(o => (
                  <tr key={o.id} onClick={() => navigate(`/orders/${o.id}`)}>
                    <td><strong>{highlight(`#${o.fileNumber}`)}</strong></td>
                    <td>{o.fileTypeName || '—'}</td>
                    <td>{fmtDate(o.dateOrdered)}</td>
                    <td><strong>{highlight(o.borrowerLastName)}</strong></td>
                    <td>{highlight(o.loanNumber) || '—'}</td>
                    <td>{o.state || '—'}</td>
                    <td>{fmtDate(o.closingDate)}</td>
                    <td>{o.statusName ? <span className={`badge ${statusClass(o.statusName)}`}>{o.statusName}</span> : '—'}</td>
                    <td style={{ color: '#888' }}>{o.assignedToName || '—'}</td>
                    <td>{fmtCurrency(o.loanAmount)}</td>
                    <td>{highlight(o.lender)}</td>
                    <td>{o.broker || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
